import { useRef, useState } from 'react'
import NewLandingPageHead from '../components/NewEliteLandingPageHead'
import NewEliteLandingKeyFeaturesCarousel from '../components/NewEliteLandingKeyFeaturesCarousel'
import NewEliteLandingPageBenefits from '../components/NewEliteLandingPageBenefits'
import NewEliteLandingTestimonialsCarousel from '../components/NewEliteLandingTestimonialsCarousel'
import NewEliteLandingPlanSelector from '../components/NewEliteLandingPlanSelector'
import NewEliteLandingTrustMetrics from '../components/NewEliteLandingTrustMetrics'
import NewEliteLandingTrustStatement from '../components/NewEliteLandingTrustStatement'
import NewEliteLandingPriceComparisonSection from '../components/NewEliteLandingPriceComparisonSection'
import VideoTrustMarker from '../components/VideoTrustMarker'
import CardsTrustMarker from '../components/CardsTrustMarker'
import NewEliteEliteInsightsTiles from '../components/NewEliteEliteInsightsTiles'
import { EliteLandingWebviewBackBar } from '../components/EliteLandingWebviewBackBar'
import type { EliteLandingPlan } from '../planTypes'
import { getBottomPadding, getTopPadding } from '../../../utils/sessionStorage'
import { useEliteOnboardingAdvisoryStatus } from '../hooks/useEliteOnboardingAdvisoryStatus'
import { getEliteBuyCtaEventExtras } from '../../../utils/eliteEventExtras'
import { EliteBuyCtaExtrasProvider } from '../../../components/elite/EliteBuyCtaExtrasContext'
import { useSyncEliteLandingPlanFromEligiblePlans } from '../hooks/useSyncEliteLandingPlanFromEligiblePlans'
import { getPlanSelectorEliteLandingPageState } from '../eliteLandingPlanSelectorState'
import {
    defaultNonFamilyOnboardingViewProps,
    type FamilyOnboardingViewProps
} from './family/familyOnboardingViewProps'
import { NewEliteEliteInsightsTilesSkeleton } from '../components/NewEliteEliteInsightsTilesSkeleton'
import {
    NewEliteLandingAnalyticsProvider,
    useTrackNewEliteLandingScreenLoadWhenReady
} from '../analytics/NewEliteLandingAnalyticsContext'
import {
    NewEliteLandingAnalyticsScreen,
    type NewEliteLandingAnalyticsScreenType
} from '../analytics/newEliteLandingAnalytics'
import NewEliteLandingFAQs from '../components/NewEliteLandingFAQs'
import { useStickyVisibilityBySentinel } from '../hooks/useStickyVisibilityBySentinel'
import {
    isEliteAutoPayActive,
    shouldShowEliteRenewalPrompt
} from '../../../components/elite/dashboard/eliteMembershipRenewalWindow'
import { EliteLandingPageState } from '../../../types/misc.types'
import { EliteAboutVideoFab } from '../../../components/elite/EliteAboutVideoFab'
import { useIsClearOfBottomFabZone } from '../../../components/elite/useIsClearOfBottomFabZone'

type NewEliteLandingPageProps = {
    familyConfig?: FamilyOnboardingViewProps
    analyticsScreen?: NewEliteLandingAnalyticsScreenType
}

function NewEliteLandingPageContent({
    familyConfig = defaultNonFamilyOnboardingViewProps
}: {
    familyConfig: FamilyOnboardingViewProps
}) {
    const [selectedPlan, setSelectedPlan] = useState<EliteLandingPlan>('yearly')
    const [isPrimaryCtaDisabled, setIsPrimaryCtaDisabled] = useState(true)
    const { data, isLoading } = useEliteOnboardingAdvisoryStatus('NEW_ELITE_ONBOARDING_ADVISORY_STATUS')
    const eligiblePlans = data?.eligiblePlans ?? []
    const planSelectorLandingState = getPlanSelectorEliteLandingPageState(data?.eliteLandingPageState)
    const showRenewalPrompt = shouldShowEliteRenewalPrompt(data)
    const isAutoPayActive = isEliteAutoPayActive(data)
    const isEliteRenewalFlow =
        showRenewalPrompt ||
        (!isAutoPayActive && data?.eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED)
    const { isStickyVisible: isPrimaryCtaStickyVisible, sentinelRef } = useStickyVisibilityBySentinel()
    const floatingPrimaryCtaRef = useRef<HTMLDivElement>(null)
    const inlinePrimaryCtaRef = useRef<HTMLDivElement>(null)
    const scrollRootRef = useRef<HTMLDivElement>(null)
    const showAboutVideoFab = useIsClearOfBottomFabZone(inlinePrimaryCtaRef, scrollRootRef, !isLoading)

    useSyncEliteLandingPlanFromEligiblePlans(eligiblePlans, setSelectedPlan, data?.selectedPlanDetails)
    useTrackNewEliteLandingScreenLoadWhenReady(!isLoading, data?.eliteLandingPageState, getEliteBuyCtaEventExtras(data))

    return (
        <EliteBuyCtaExtrasProvider status={data}>
            <div
                ref={scrollRootRef}
                style={{
                    paddingTop: `calc(${getTopPadding()}px + 8px)`,
                    paddingBottom: `calc(${getBottomPadding()}px + 88px)`
                }}
                className='h-screen overflow-y-auto no-scrollbar overflow-x-hidden overscroll-none bg-[#FFF2DF] z-0 relative'
            >
                <div className='absolute -top-16 left-0 z-[-1] w-full'>
                    <img src='/images/new-elite/new-elite-bg.svg' alt='elite-bg' className='w-full h-auto object-cover' />
                </div>
                <EliteLandingWebviewBackBar isStickyVisible={isPrimaryCtaStickyVisible} />
                <div>
                    <NewLandingPageHead />
                    <NewEliteLandingTrustMetrics />
                    <div className='w-full'>
                        <NewEliteLandingPlanSelector
                            familyConfig={familyConfig}
                            selectedPlan={selectedPlan}
                            onPlanChange={setSelectedPlan}
                            eligiblePlans={eligiblePlans}
                            cta={isEliteRenewalFlow ? data?.renewal?.cta : data?.cta}
                            eliteLandingPageState={planSelectorLandingState}
                            selectedPlanDetails={data?.selectedPlanDetails}
                            activationExpiryDate={data?.subscriptionDetails?.activationExpiryDate}
                            autoPayStatus={data?.subscriptionDetails?.autoPayStatus}
                            renewalStatus={data?.renewal?.status}
                            consentText={data?.consentText}
                            consentTextList={data?.consentTextList}
                            isStatusLoading={isLoading}
                            eliteRenewalFlow={isEliteRenewalFlow}
                            enableFloatingPrimaryCta
                            isFloatingPrimaryCtaVisible={isPrimaryCtaStickyVisible}
                            floatingPrimaryCtaRef={floatingPrimaryCtaRef}
                            inlinePrimaryCtaRef={inlinePrimaryCtaRef}
                            onPrimaryCtaDisabledChange={setIsPrimaryCtaDisabled}
                            referralReward={data?.referralReward}
                        />
                    </div>
                    <div ref={sentinelRef} />
                </div>


                <NewEliteLandingPageBenefits
                    rebalanceSection={{
                        isStatusLoading: isLoading,
                        cta: isEliteRenewalFlow ? data?.renewal?.cta : data?.cta,
                        eligiblePlans,
                        selectedPlan,
                        isCtaDisabled: isPrimaryCtaDisabled,
                        numberOfFundsToRebalance: data?.numberOfFundsToRebalance ?? 0,
                        missedGains: data?.missedGains ?? 0,
                        isForceUpdateRequired: data?.isForceUpdateRequired ?? false,
                        familyConfig
                    }}
                />
                <div className='relative z-0 bg-white px-5 pt-20'>
                    <div className='absolute -top-[310px] -right-6 bottom-0 bg-white z-[-1]'>
                        <img src='/images/new-elite/wavy-lines.svg' alt='elite-benefits-lines' className='w-[500px] h-auto object-cover' />
                    </div>
                    <NewEliteLandingKeyFeaturesCarousel selectedPlan={selectedPlan} />
                    <div className='relative z-0 pt-8'>
                        {isLoading ? (
                            <NewEliteEliteInsightsTilesSkeleton />
                        ) : data?.eliteInsights ? (
                            <div className='mt-2 w-full'>
                                <NewEliteEliteInsightsTiles
                                    eliteInsights={data?.eliteInsights}
                                    numberOfFundsToRebalance={data?.numberOfFundsToRebalance ?? 0}
                                />
                            </div>
                        ) : null}
                        <div className='absolute bottom-36 -right-6 bottom-0 bg-white z-[-1]'>
                            <img src='/images/new-elite/wavy-lines.svg' alt='elite-benefits-lines' className='w-[500px] h-auto object-cover' />
                        </div>
                        <NewEliteLandingTestimonialsCarousel />
                        <div className='flex flex-col pt-24 pb-1'>
                            <div className='pb-20'>
                                <VideoTrustMarker />
                            </div>
                            <div className=''>
                                <CardsTrustMarker />
                            </div>
                        </div>
                        <NewEliteLandingPriceComparisonSection
                            eligiblePlans={eligiblePlans}
                            selectedPlan={selectedPlan}
                            selectedPlanDetails={data?.selectedPlanDetails}
                            isStatusLoading={isLoading}
                        />
                    </div>
                </div>
                <NewEliteLandingFAQs isFamilyMode={familyConfig.isFamilyMode} />
                <NewEliteLandingTrustStatement />
                <EliteAboutVideoFab
                    showFab={showAboutVideoFab}
                    isEliteCtaVisible={isPrimaryCtaStickyVisible}
                    eliteCtaRef={floatingPrimaryCtaRef}
                    eliteCtaBottomPx={getBottomPadding() + 16}
                />
            </div>
        </EliteBuyCtaExtrasProvider>
    )
}

export default function NewEliteLandingPage({
    familyConfig = defaultNonFamilyOnboardingViewProps,
    analyticsScreen = NewEliteLandingAnalyticsScreen.Landing
}: NewEliteLandingPageProps) {
    return (
        <NewEliteLandingAnalyticsProvider screen={analyticsScreen}>
            <NewEliteLandingPageContent familyConfig={familyConfig} />
        </NewEliteLandingAnalyticsProvider>
    )
}
